import { useState, useEffect } from "react";
import axios from "axios";
import { useParams, useNavigate, Link } from "react-router-dom";
import getAPIUrl from "./API_FIX";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = getAPIUrl(BACKEND_URL);

const EditScore = () => {
  const { scoreId } = useParams();
  const navigate = useNavigate();
  const [score, setScore] = useState(null);
  const [match, setMatch] = useState(null);
  const [matchConfig, setMatchConfig] = useState(null);
  const [shooter, setShooter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  // Form state for the score being edited
  const [formData, setFormData] = useState({
    stages: []
  });
  
  useEffect(() => {
    fetchData();
  }, [scoreId]);
  
  // Function to match stage names between score and match config
  const findMatchingMatchType = (score, matchConfig) => {
    if (!score || !matchConfig || !matchConfig.match_types) return null;
    
    // First try to find the match type by instance name
    const matchType = matchConfig.match_types.find(mt => 
      mt.instance_name === score.match_type_instance
    );
    
    if (matchType) return matchType;
    
    // If not found, try to find by comparing stages
    const scoreStageNames = score.stages.map(s => s.name);
    
    // Check if the score stages are a subset of any match type's entry stages
    // or if the entry stages are similar enough (e.g., "SF" vs "SF1")
    return matchConfig.match_types.find(mt => {
      // Check if all score stages are contained in this match type's entry stages
      // or if they follow a similar pattern (e.g., score has "SF", match has "SF1")
      return scoreStageNames.every(stageName => 
        mt.entry_stages.includes(stageName) || 
        mt.entry_stages.some(entryStage => entryStage.startsWith(stageName) || 
                            stageName.startsWith(entryStage.replace(/\d+$/, "")))
      );
    });
  };

  const handleStageChange = (stageIdx, field, value) => {
    // Immutable update (never mutate shared stage objects), and guard against
    // NaN from partial/invalid input — empty means an intentionally skipped stage.
    setFormData((prev) => ({
      ...prev,
      stages: prev.stages.map((st, j) => {
        if (j !== stageIdx) return st;
        if (value === "") return { ...st, [field]: null };
        const parsed = parseInt(value, 10);
        return { ...st, [field]: Number.isNaN(parsed) ? null : parsed };
      }),
    }));
  };
  
  const fetchData = async () => {
    try {
      if (!scoreId) {
        setError("Score ID is missing. Cannot edit this score.");
        setLoading(false);
        return;
      }
      
      // Get the token from localStorage
      const token = localStorage.getItem('token');
      if (!token) {
        setError("Authentication required. Please log in again.");
        setLoading(false);
        return;
      }

      // Set authorization header
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };
      // Fetch score data
      const scoreResponse = await axios.get(`${API}/scores/${scoreId}`, config);
      setScore(scoreResponse.data);
      
      // Fetch match details
      const matchResponse = await axios.get(`${API}/matches/${scoreResponse.data.match_id}`, config);
      setMatch(matchResponse.data);
      
      // Fetch match configuration
      const configResponse = await axios.get(`${API}/match-config/${scoreResponse.data.match_id}`, config);
      setMatchConfig(configResponse.data);
      
      // Fetch shooter details
      const shooterResponse = await axios.get(`${API}/shooters/${scoreResponse.data.shooter_id}`, config);
      setShooter(shooterResponse.data);
      
      // Initialize form data with the score
      setFormData({
        shooter_id: scoreResponse.data.shooter_id,
        match_id: scoreResponse.data.match_id,
        match_type_instance: scoreResponse.data.match_type_instance,
        caliber: scoreResponse.data.caliber,
        stages: scoreResponse.data.stages,
        fired_twentytwo: scoreResponse.data.fired_twentytwo || false
      });
      
      setLoading(false);
    } catch (err) {
      console.error("Error fetching data:", err);
      let errorMessage = "Failed to load required data. Please try again.";
      if (err.response) {
        console.error("Error response:", err.response.data);
        if (err.response.data && err.response.data.detail) {
          errorMessage = `Error: ${err.response.data.detail}`;
        }
      }
      setError(errorMessage);
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Get the token from localStorage
      const token = localStorage.getItem('token');
      if (!token) {
        setError("Authentication required. Please log in again.");
        return;
      }

      // Set authorization header
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };
      
      const response = await axios.put(`${API}/scores/${scoreId}`, formData, config);
      setSuccess(true);
      setTimeout(() => {
        navigate(`/matches/${formData.match_id}`);
      }, 2000);
    } catch (err) {
      let errorMessage = "Failed to update score. Please try again.";
      if (err.response) {
        if (err.response.data && err.response.data.detail) {
          errorMessage = `Error: ${err.response.data.detail}`;
        }
      }
      setError(errorMessage);
      setLoading(false);
    }
  };

  // Helper to get the matching match type from config
  
  if (!score || !match || !matchConfig || !shooter) {
    return <div className="page text-center text-ink-500">Score data not found</div>;
  }
  
  // Get the matching match type
  const matchingMatchType = findMatchingMatchType(score, matchConfig);
  if (!matchingMatchType) {
    return (
      <div className="page text-center">
        <div className="alert-error mb-4 text-left">
          Could not find a matching match type for this score. The stage names may not match the current match configuration.
        </div>
        <pre className="text-left bg-ink-100 text-ink-700 p-4 rounded-lg overflow-auto">
          {JSON.stringify({ 
            scoreStages: score.stages.map(s => s.name),
            matchTypes: matchConfig.match_types.map(mt => ({
              name: mt.instance_name,
              stages: mt.entry_stages
            }))
          }, null, 2)}
        </pre>
      </div>
    );
  }

  if (success) {
    return (
      <div className="page">
        <div className="alert-success text-center">
          <h2 className="text-xl font-bold mb-2">Score Updated Successfully!</h2>
          <p>Redirecting to match details...</p>
        </div>
      </div>
    );
  }

  // Find the match type configuration
  const matchTypeObj = matchingMatchType || matchConfig.match_types.find(mt => mt.instance_name === formData.match_type_instance);
  const maxScore = matchTypeObj ? matchTypeObj.max_score : 0;

  // Calculate subtotals based on stage values
  const calculateSubtotals = () => {
    const subtotals = {};
    
    if (matchTypeObj && matchTypeObj.subtotal_mappings && Object.keys(matchTypeObj.subtotal_mappings).length > 0) {
      for (const [subtotalName, sourceStages] of Object.entries(matchTypeObj.subtotal_mappings)) {
        let subtotalScore = 0;
        let subtotalXCount = 0;
        
        formData.stages.forEach(stage => {
          if (sourceStages.includes(stage.name)) {
            if (stage.score !== null) {
              subtotalScore += stage.score;
            }
            if (stage.x_count !== null) {
              subtotalXCount += stage.x_count;
            }
          }
        });
        
        subtotals[subtotalName] = {
          score: subtotalScore,
          x_count: subtotalXCount
        };
      }
    }
    
    return subtotals;
  };

  // Calculate total score and X count
  const calculateTotals = () => {
    return {
      totalScore: formData.stages.reduce((sum, stage) => 
        stage.score !== null ? sum + stage.score : sum, 0),
      totalXCount: formData.stages.reduce((sum, stage) => 
        stage.x_count !== null ? sum + stage.x_count : sum, 0)
    };
  };

  const subtotals = calculateSubtotals();
  const { totalScore, totalXCount } = calculateTotals();

  return (
    <div className="page">
      <div className="mb-6 flex items-center justify-between gap-4">
        <div>
          <h1 className="page-title">Edit Score</h1>
          <p className="page-subtitle">
            Match: {match.name} ({new Date(match.date).toLocaleDateString()})
          </p>
          <p className="page-subtitle">
            Shooter: {shooter.name}
          </p>
          <p className="page-subtitle">
            Match Type: {matchTypeObj ? matchTypeObj.type : "Unknown"} - {formData.caliber}
          </p>
        </div>
        <Link to={`/matches/${match.id}`} className="btn-secondary">
          Back to Match
        </Link>
      </div>

      <div className="card card-pad">
        <form onSubmit={handleSubmit} className="space-y-6">
          {formData.caliber !== ".22" && (
            <label className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-ink-300 text-amber-600 focus:ring-amber-500"
                checked={!!formData.fired_twentytwo}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    fired_twentytwo: e.target.checked,
                  }))
                }
              />
              <span>
                <span className="font-medium">.22 only</span> — fired a .22 for
                this {formData.caliber} card (scored separately from true{" "}
                {formData.caliber} shooters)
              </span>
            </label>
          )}
          {/* Entry Stages */}
          <div className="mb-6">
            <h5 className="section-title mb-3">Entry Stages</h5>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {formData.stages.map((stage, stageIdx) => (
                <div key={stageIdx} className="rounded-xl border border-ink-200 p-3 transition-shadow hover:shadow-card">
                  <h5 className="font-semibold text-ink-800 mb-2">{stage.name}</h5>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="field-label">
                        Score
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={stage.score === null ? "" : stage.score}
                        onChange={(e) => handleStageChange(stageIdx, 'score', e.target.value)}
                        className="input"
                        placeholder="-"
                      />
                    </div>
                    <div>
                      <label className="field-label">
                        X Count
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="10"
                        value={stage.x_count === null ? "" : stage.x_count}
                        onChange={(e) => handleStageChange(stageIdx, 'x_count', e.target.value)}
                        className="input"
                        placeholder="-"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Subtotals Section - Only shown for match types with subtotals */}
          {Object.keys(subtotals).length > 0 && (
            <div className="mb-6">
              <h5 className="section-title mb-3">Subtotals (Automatically Calculated)</h5>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.entries(subtotals).map(([subtotalName, values]) => (
                  <div key={subtotalName} className="rounded-xl border border-ink-200 bg-ink-50 p-3">
                    <h5 className="font-semibold text-ink-800 mb-2">{subtotalName}</h5>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="field-label">
                          Score
                        </label>
                        <div className="w-full rounded-lg border border-ink-200 bg-ink-100 px-3.5 py-2 font-semibold text-ink-900">
                          {values.score}
                        </div>
                      </div>
                      <div>
                        <label className="field-label">
                          X Count
                        </label>
                        <div className="w-full rounded-lg border border-ink-200 bg-ink-100 px-3.5 py-2 font-semibold text-ink-900">
                          {values.x_count}
                        </div>
                      </div>
                    </div>
                    {matchTypeObj.subtotal_mappings[subtotalName] && (
                      <div className="mt-2 text-xs text-ink-500">
                        Sum of: {matchTypeObj.subtotal_mappings[subtotalName].join(', ')}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Grand Total */}
          <div className="mt-4 rounded-xl border border-ink-200 bg-ink-50 p-4">
            <h5 className="section-title mb-2">Total</h5>
            <div className="flex justify-between items-center">
              <div>
                <span className="text-lg font-bold text-ink-900">
                  {formData.stages.every(stage => stage.score === null) ? "-" : totalScore}
                </span>
                {!formData.stages.every(stage => stage.score === null) && (
                  <>
                    <span className="text-ink-500"> / {maxScore}</span>
                    <span className="ml-4 text-ink-500">X Count: {formData.stages.every(stage => stage.x_count === null) ? "-" : totalXCount}</span>
                  </>
                )}
              </div>
              {!formData.stages.every(stage => stage.score === null) && (
                <div className="badge-brand">
                  {Math.round((totalScore / maxScore) * 100)}%
                </div>
              )}
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end mt-8">
            <button
              type="submit"
              className="btn-primary"
            >
              Update Score
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditScore;
